import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { generateHTML } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';

const C = {
  blue: '#6699ff',
  navy: '#0b1120',
  cream: '#f5f0e8',
  creamLight: '#faf7f2',
  creamWarm: '#ede5d8',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#6b7a92',
  white: '#ffffff',
};

export default function ArticlePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [files, setFiles] = useState([]);

  useEffect(() => {
    loadArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadArticle = async () => {
    const { data } = await supabase
      .from('articles')
      .select('*')
      .eq('id', id)
      .single();

    if (data) {
      setArticle(data);
      if (data.content) {
        try {
          const html = generateHTML(data.content, [
            StarterKit,
            Image,
            Underline,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Link,
            Highlight,
          ]);
          setHtmlContent(html);
        } catch (e) {
          setHtmlContent('<p>Error rendering content</p>');
        }
      }
    }

    const { data: fileData } = await supabase
      .from('article_files')
      .select('*')
      .eq('article_id', id);

    if (fileData) setFiles(fileData);
  };

  if (!article) {
    return (
      <div style={{ minHeight: '100vh', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.cream }}>
        Loading preview...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.creamLight, fontFamily: "'Manrope', sans-serif" }}>
      {/* Preview Banner */}
      <div style={{
        background: C.navy, padding: '12px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.blue }}>
            Preview Mode
          </span>
          <span style={{
            padding: '3px 10px', fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
            textTransform: 'uppercase',
            background: article.status === 'published' ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)',
            color: article.status === 'published' ? '#4ade80' : '#facc15',
          }}>
            {article.status}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => navigate(`/spc-portal-1003/articles/${id}/edit`)}
            style={{
              padding: '8px 20px', border: `1px solid rgba(245,240,232,0.2)`, background: 'transparent',
              color: C.cream, fontFamily: "'Manrope', sans-serif", fontSize: 11,
              fontWeight: 600, letterSpacing: 1, cursor: 'pointer',
            }}
          >
            Back to Editor
          </button>
          <button
            onClick={() => navigate('/spc-portal-1003')}
            style={{
              padding: '8px 20px', border: 'none', background: C.blue,
              color: C.navy, fontFamily: "'Manrope', sans-serif", fontSize: 11,
              fontWeight: 700, letterSpacing: 1, cursor: 'pointer',
            }}
          >
            Dashboard
          </button>
        </div>
      </div>

      {/* Cover Image */}
      {article.cover_image && (
        <div style={{ width: '100%', height: 400, overflow: 'hidden' }}>
          <img src={article.cover_image} alt={article.title} style={{
            width: '100%', height: '100%', objectFit: 'cover',
          }} />
        </div>
      )}

      {/* Article Content */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '60px 24px 100px' }}>
        {/* Meta */}
        <div style={{ marginBottom: 32 }}>
          {article.category && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase',
              color: C.blue, display: 'block', marginBottom: 16,
            }}>
              {article.category}
            </span>
          )}
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: 48, fontWeight: 500,
            color: C.textDark, lineHeight: 1.15, marginBottom: 20,
          }}>
            {article.title}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13, color: C.textMuted }}>
            <span>{article.author}</span>
            <span>·</span>
            <span>{new Date(article.published_at || article.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
          {article.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              {article.tags.map(tag => (
                <span key={tag} style={{
                  padding: '4px 14px', background: 'rgba(102,153,255,0.06)',
                  border: '1px solid rgba(102,153,255,0.12)',
                  fontSize: 11, fontWeight: 600, color: C.blue,
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: C.creamWarm, marginBottom: 40 }} />

        {/* Body */}
        <div
          className="article-body"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />

        {/* Files */}
        {files.length > 0 && (
          <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${C.creamWarm}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.blue, marginBottom: 16 }}>
              Attachments
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {files.map(file => (
                <div key={file.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px', background: C.white, border: `1px solid ${C.creamWarm}`,
                }}>
                  <span style={{
                    padding: '2px 8px', background: 'rgba(102,153,255,0.08)',
                    fontSize: 10, fontWeight: 700, color: C.blue, letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}>
                    {file.file_name.split('.').pop()}
                  </span>
                  <span style={{ fontSize: 13, color: C.textDark, fontWeight: 500 }}>{file.file_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Article body styles */}
      <style>{`
        .article-body { font-size: 16px; line-height: 1.85; color: ${C.textBody}; }
        .article-body h1 { font-family: 'Cormorant Garamond', serif; font-size: 32px; font-weight: 600; margin: 32px 0 16px; color: ${C.navy}; }
        .article-body h2 { font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 600; margin: 28px 0 14px; color: ${C.navy}; }
        .article-body h3 { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 600; margin: 24px 0 10px; color: ${C.navy}; }
        .article-body p { margin: 0 0 16px; }
        .article-body ul, .article-body ol { padding-left: 24px; margin: 0 0 16px; }
        .article-body li { margin: 6px 0; }
        .article-body blockquote { border-left: 3px solid ${C.blue}; padding-left: 20px; margin: 20px 0; color: ${C.textBody}; font-style: italic; }
        .article-body img { max-width: 100%; height: auto; margin: 20px 0; border: 1px solid ${C.creamWarm}; }
        .article-body a { color: ${C.blue}; text-decoration: underline; }
        .article-body hr { border: none; border-top: 1px solid ${C.creamWarm}; margin: 32px 0; }
        .article-body mark { background: rgba(102,153,255,0.2); padding: 2px 6px; }
      `}</style>
    </div>
  );
}